import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function SortIcon({ column, sortCol, sortDir }) {
  if (sortCol !== column) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-40" />;
  return sortDir === "asc"
    ? <ArrowUp className="ml-1 inline h-3 w-3 text-[#323c44]" />
    : <ArrowDown className="ml-1 inline h-3 w-3 text-[#323c44]" />;
}

export default function ChurnReportSection({ churnReport, loading, formatCurrency, formatMonthLabel, palette }) {
  const [expandedDeals, setExpandedDeals] = useState(new Set());
  const [sortCol, setSortCol] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");

  const toggleDeal = (dealId) => {
    setExpandedDeals((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir(col === "revenue" ? "desc" : "asc");
    }
  };

  const sortDeals = (deals) => {
    if (!deals) return [];
    return [...deals].sort((a, b) => {
      let av, bv;
      if (sortCol === "company") {
        av = (a.company_name || "").toLowerCase();
        bv = (b.company_name || "").toLowerCase();
      } else if (sortCol === "deal") {
        av = (a.deal_name || "").toLowerCase();
        bv = (b.deal_name || "").toLowerCase();
      } else if (sortCol === "renewal") {
        av = a.renewal_date || "";
        bv = b.renewal_date || "";
      } else if (sortCol === "products") {
        av = Object.keys(a.products || {}).join(", ").toLowerCase();
        bv = Object.keys(b.products || {}).join(", ").toLowerCase();
      } else {
        av = a.total_churn_revenue ?? a.total_churn_mrr ?? 0;
        bv = b.total_churn_revenue ?? b.total_churn_mrr ?? 0;
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  };

  if (loading) {
    return (
      <section className="mt-10">
        <Card className="border-[#dddddd] bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="font-sans text-xl font-bold text-[#323c44]">Churn Report</CardTitle>
            <CardDescription className="font-sans text-sm text-[#4a5b68]">
              Revenue at risk after renewal dates, by product and month.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-16">
              <p className="text-sm text-[#4a5b68]">Loading churn data…</p>
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  const hasData = churnReport && churnReport.deals && churnReport.deals.length > 0;
  const products = churnReport?.products || [];
  const months = churnReport?.months || [];

  const chartData = months.map((m) => ({
    label: formatMonthLabel(m.month),
    month: m.month,
    total_mrr: m.total_mrr,
    ...m.products,
  }));

  const totalAtRisk = (churnReport?.deals || []).reduce((s, d) => s + (d.total_churn_revenue ?? d.total_churn_mrr ?? 0), 0);
  const sortedDeals = sortDeals(churnReport?.deals);

  return (
    <section className="mt-10" data-testid="churn-report-section">
      <Card className="border-[#dddddd] bg-white shadow-sm">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="font-sans text-xl font-bold text-[#323c44]">Churn Report</CardTitle>
              <CardDescription className="mt-1 font-sans text-sm text-[#4a5b68]">
                Revenue at risk after renewal dates — recurring revenue that would be lost if deals are not renewed.
              </CardDescription>
            </div>
            {hasData && (
              <div className="shrink-0 rounded-md bg-[#e8faf5] px-4 py-2 text-right">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#b45309]">Total at-risk revenue</p>
                <p className="text-xl font-bold tabular-nums text-[#92400e]">{formatCurrency(totalAtRisk)}</p>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!hasData ? (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="churn-empty-state">
              <p className="font-sans text-lg text-[#323c44]">No churn data found.</p>
              <p className="mt-2 max-w-md text-sm text-[#4a5b68]">
                Ensure deals have a <span className="font-medium text-[#323c44]">Renewal Date</span> property set in
                HubSpot and re-sync. Only recurring line items active at or after the renewal date are included.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* ── Chart ── */}
              {chartData.some((d) => d.total_mrr > 0) && (
                <div>
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">
                    At-risk revenue by month
                  </p>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#efefef" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#7d8b96" }} axisLine={false} tickLine={false} />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#7d8b96" }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => formatCurrency(v, true)}
                        width={68}
                      />
                      <Tooltip
                        formatter={(value, name) => [formatCurrency(value), name]}
                        contentStyle={{ borderRadius: "8px", border: "1px solid #dddddd", fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                      />
                      {products.length > 1 && <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8, color: "#4a5b68" }} />}
                      {products.map((product, i) => (
                        <Bar
                          key={product}
                          dataKey={product}
                          stackId="churn"
                          fill={palette[i % palette.length]}
                          radius={i === products.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* ── Deal table ── */}
              <div className="overflow-x-auto rounded-md border border-[#dddddd]">
                <table className="w-full text-sm" data-testid="churn-deals-table">
                  <thead>
                    <tr className="border-b border-[#dddddd] bg-[#f5f5f5]">
                      <th className="w-8 px-3 py-3" />
                      {[
                        { key: "company", label: "Company", align: "left" },
                        { key: "deal",    label: "Deal",    align: "left" },
                        { key: "renewal", label: "Renewal Date", align: "left" },
                        { key: "products",label: "Products", align: "left" },
                        { key: "revenue", label: "At-Risk Revenue", align: "right" },
                      ].map(({ key, label, align }) => (
                        <th
                          key={key}
                          className={`cursor-pointer select-none px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68] hover:text-[#323c44] ${align === "right" ? "text-right" : "text-left"}`}
                          onClick={() => handleSort(key)}
                        >
                          {label}
                          <SortIcon column={key} sortCol={sortCol} sortDir={sortDir} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDeals.map((deal, i) => {
                      const isOpen = expandedDeals.has(deal.deal_id);
                      const dealRevenue = deal.total_churn_revenue ?? deal.total_churn_mrr ?? 0;
                      const rowBg = i % 2 === 0 ? "bg-white" : "bg-[#f5f5f5]";
                      return (
                        <>
                          {/* ── Deal row ── */}
                          <tr
                            key={deal.deal_id}
                            className={`cursor-pointer border-b border-[#efefef] transition hover:bg-[#f5f5f5] ${isOpen ? "bg-[#f0f9ff]" : rowBg}`}
                            onClick={() => toggleDeal(deal.deal_id)}
                          >
                            <td className="px-3 py-3 text-[#7d8b96]">
                              {isOpen
                                ? <ChevronDown className="h-4 w-4" />
                                : <ChevronRight className="h-4 w-4" />}
                            </td>
                            <td className="px-5 py-3 font-medium text-[#323c44]">{deal.company_name}</td>
                            <td className="px-5 py-3 text-[#4a5b68]">
                              {deal.deal_url ? (
                                <a
                                  href={deal.deal_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#0052cc] underline-offset-2 hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {deal.deal_name || deal.deal_id}
                                </a>
                              ) : (
                                deal.deal_name || deal.deal_id
                              )}
                            </td>
                            <td className="px-5 py-3 tabular-nums text-[#4a5b68]">{formatDate(deal.renewal_date)}</td>
                            <td className="px-5 py-3 text-[#4a5b68]">{Object.keys(deal.products || {}).join(", ") || "—"}</td>
                            <td className="px-5 py-3 text-right font-semibold tabular-nums text-[#c2192b]">
                              {formatCurrency(dealRevenue)}
                            </td>
                          </tr>

                          {/* ── Breakdown rows ── */}
                          {isOpen && (deal.line_items || []).map((li) => (
                            <tr
                              key={`${deal.deal_id}-${li.line_item_name}`}
                              className="border-b border-[#efefef] bg-[#f8fbff]"
                            >
                              <td className="px-3 py-2" />
                              <td className="px-5 py-2" />
                              <td className="px-5 py-2 text-xs text-[#4a5b68]">
                                <span className="ml-3 font-medium text-[#323c44]">{li.line_item_name}</span>
                              </td>
                              <td className="px-5 py-2 text-xs text-[#7d8b96]" />
                              <td className="px-5 py-2 text-xs text-[#7d8b96]">
                                {formatCurrency(li.monthly_mrr)}/mo × {li.months_count} month{li.months_count !== 1 ? "s" : ""}
                              </td>
                              <td className="px-5 py-2 text-right text-xs font-medium tabular-nums text-[#c2192b]">
                                {formatCurrency(li.total_revenue)}
                              </td>
                            </tr>
                          ))}
                        </>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#dddddd] bg-[#f5f5f5]">
                      <td />
                      <td className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">
                        Total ({churnReport.deals.length} deal{churnReport.deals.length !== 1 ? "s" : ""})
                      </td>
                      <td /><td /><td />
                      <td className="px-5 py-3 text-right font-bold tabular-nums text-[#c2192b]">
                        {formatCurrency(totalAtRisk)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
